import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { OneFetchyeProvider } from 'fetchye-one-app';
import { OneExperimentsProvider, prefetchExperiments } from '@americanexpress/one-experiments';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { withCookies, Cookies } from 'react-cookie';
import { IntlProvider } from 'react-intl';
import SharedScript, { Adobe, Contentsquare, Dynatrace } from '@americanexpress/shared-scripts';
import DataManager from '@americanexpress/data-manager';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { UncaughtErrorReporter, IguazuErrorReporter, DebugErrorReportingBrowserSessionUUID } from '@americanexpress/travel-error-base';
import { getEnvironmentSelector } from '@americanexpress/travel-selectors';
import { useSelector } from 'react-redux';
import reducer from '../ducks/ducks';
import useSetTltsIdHook from '../hooks/useSetTltsIdHook';
import OfflineWarning from './OfflineWarning/OfflineWarning';
import CookieHandler from './CookieHandler/CookieHandler';
import DigitalDataLayer from './DigitalDataLayer/DigitalDataLayer';
import AmexDDLViewHandler from './ComponentWithPageUpdate/AmexDDLViewHandler';
import childRoutes from '../childRoutes';
import PageMeta from './PageMeta/PageMeta';
import Tealeaf from './Tealeaf/Tealeaf';
import useAmexSessionHook from '../hooks/useAmexSessionhook';
import MODULE_NAME from '../constants/moduleName';
import useLocationSyncWithLocaleHook from '../hooks/useLocationSyncWithLocaleHook';
import { getIguazuResourcesSelector } from '../selectors/getModuleStateSelectors';
import ClickToChat from './ClickToChat/ClickToChat';
import useEnsighten from '../hooks/useEnsighten';
import { appId as appIdE2 } from '../generated/dynatraceAttrs.json';
import { appId as appIdE3 } from '../generated/dynatraceAttrsE3.json';
import './TravelRoot.scss';
import useTravelDynatrace from './TravelDynatrace';

const TravelRoot = ({
  location,
  cookies,
  locale,
  languageData,
  children,
  routes,
  router,
}) => {
  useSetTltsIdHook(location, cookies);
  useAmexSessionHook();
  useLocationSyncWithLocaleHook(router, location);
  useEnsighten(locale);
  const currentRoute = routes && routes.length > 0 && routes[routes.length - 1];
  const [isHydrated, setHydration] = useState();
  const environment = useSelector(getEnvironmentSelector);

  /* istanbul ignore next -- unable to mock the Intl object in testing */
  useEffect(() => {
    /* eslint-disable no-underscore-dangle -- what */
    if (typeof Intl?.__disableRegExpRestore === 'function') {
      Intl.__disableRegExpRestore();
    }
    /* eslint-enable no-underscore-dangle -- what */
  }, []);

  /* istanbul ignore next -- too much effort to move to RTL to test this */
  useEffect(() => {
    let timeout;
    if (!isHydrated) {
      timeout = setTimeout(() => {
        setHydration(true);
      }, 0);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  useTravelDynatrace();

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <div id="hydration-target" className="display-none" data-isHydrated={isHydrated} />
      <DebugErrorReportingBrowserSessionUUID />
      <UncaughtErrorReporter />
      <IguazuErrorReporter getToIguazuState={getIguazuResourcesSelector} />
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- Enable */}
      <DataManager {...getAmexDataManagerConfig({ initialDDL: { historyLimit: 100 } })}>
        <AmexDDLRegister disableRegisterList={{ view: true }} />
        <AmexDDLViewHandler />
        <PageMeta pageTitle={currentRoute.title} />
        <SharedScript
          name="one-identity-session"
          version="^1.0.0"
          windowCheck="AmexSession"
        />
        <SharedScript
          name="one-stream-data-handler"
          windowCheck="oneStreamData"
          version="^0.1.2"
        />
        <SharedScript // allows use of the eXPress visual editor
          name="visual-editor-sdk"
          version="^2.3.1"
          async={true}
        />
        <SharedScript // adds more tracking for eXPress experiments
          name="trackit"
          version="^1.14.0"
          async={true}
        />
        <Dynatrace
          version="^3.0.0"
          appId={environment === 'e3' ? appIdE3 : appIdE2}
          environment={environment}
        />
        <OfflineWarning />
        <CookieHandler />
        <OneExperimentsProvider
          omitQueryParamsFromLocator={true}
        >
          <DigitalDataLayer />
          <OneFetchyeProvider>
            {children}
          </OneFetchyeProvider>
          <ClickToChat />
        </OneExperimentsProvider>
        <Adobe async={true} version="^1.0.0" />
        <Contentsquare version="^1.0.2" />
        <Tealeaf />
      </DataManager>
    </IntlProvider>
  );
};

TravelRoot.childRoutes = childRoutes;

TravelRoot.propTypes = {
  children: PropTypes.node.isRequired,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({
      TLTSID: PropTypes.string,
    }).isRequired,
  }).isRequired,
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({}).isRequired,
  route: PropTypes.string.isRequired,
  updateLocale: PropTypes.func.isRequired,
  moduleLoadStatus: PropTypes.oneOf(['loading', 'loaded', 'error']).isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      getTitle: PropTypes.func,
    })
  ).isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export const loadModuleData = async ({
  store: {
    getState,
    dispatch,
  },
  ownProps: {
    location,
  },
}) => {
  const environment = getEnvironmentSelector(getState());
  if (environment !== 'e3' && location.query.disableSSR === 'true' && !global.BROWSER) {
    return;
  }

  global.getState = getState;
  try {
    const origin = getState().getIn(['browser', 'location', 'origin']);
    const { pathname } = location;
    await dispatch(prefetchExperiments({ locator: `${origin}${pathname}` }));
  } catch (error) {
    dispatch(addErrorToReport(
      error,
      {
        moduleID: MODULE_NAME,
        collectionMethod: 'loadModuleData',
      }
    ));
  }
};

TravelRoot.holocron = {
  name: MODULE_NAME,
  reducer,
  loadModuleData,
  options: {
    provideModuleState: false,
  },
};

const hocChain = compose(
  oneAppModuleWrapper(MODULE_NAME),
  withCookies
);

export { TravelRoot as TestableTravelRoot };

export default hocChain(TravelRoot);
